import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';

/**
 * Protected Route Component
 * Following the documentation guidelines for route protection
 * Ensures only authenticated users can access protected routes
 */

const ProtectedRoute = ({ children, requiredRole = null, requiredRoles = null, requiredPermission = null }) => {
  const { isAuthenticated, loading, roles, permissions } = useApp();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated → send to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Allow all access for super admin
  if (roles && roles.includes('ROLE_SUPER_ADMIN')) {
    return children;
  }

  // Enforce multiple role requirement if specified
  if (requiredRoles && Array.isArray(requiredRoles)) {
    const ok = (roles || []).some(r => requiredRoles.includes(r));
    if (!ok) return <Navigate to="/dashboard" replace />;
  }
  // Enforce role requirement if specified
  if (requiredRole && !(roles || []).includes(requiredRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Enforce permission requirement if specified
  if (requiredPermission && !(permissions || []).includes(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Authorized
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRole: PropTypes.string,
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  requiredPermission: PropTypes.string,
};

export default ProtectedRoute;
